import React from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";

// MUI's SvgIcon renders an <svg>/<path>, so the usual "gradient text" trick
// (background + -webkit-background-clip:text) doesn't apply to it — that CSS
// only clips a font glyph, not an SVG shape. A background-clip:text hack
// would silently render nothing. Instead we put the brand color/gradient on
// the button's own background (a filled chip, same idea as Instagram's own
// app-icon treatment) and keep the glyph itself plain white on top.
const INSTAGRAM_GRADIENT =
  "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)";

const PLATFORM_CONFIG = {
  facebook: { Icon: FacebookIcon, background: "#1877f2", label: "Facebook" },
  instagram: { Icon: InstagramIcon, background: INSTAGRAM_GRADIENT, label: "Instagram" },
};

/**
 * Brand-colored square icon button for Facebook/Instagram, used in both the
 * Header (desktop nav + mobile drawer) and the Footer. Restores the
 * tooltip-on-hover behavior ("Follow us on facebook"/"instagram") from the
 * legacy site's navbar.
 */
export default function SocialIconButton({ platform, href, businessName, size }) {
  const { Icon, background, label } = PLATFORM_CONFIG[platform];
  const dimension = size === "large" ? 46 : 38;

  return (
    <Tooltip title={`Follow us on ${label.toLowerCase()}`}>
      <IconButton
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={`${businessName} on ${label}`}
        sx={{
          width: dimension,
          height: dimension,
          borderRadius: 1.5,
          background,
          color: "white.main",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
        }}
      >
        <Icon sx={{ fontSize: dimension * 0.58 }} />
      </IconButton>
    </Tooltip>
  );
}

SocialIconButton.propTypes = {
  platform: PropTypes.oneOf(["facebook", "instagram"]).isRequired,
  href: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["medium", "large"]),
};

SocialIconButton.defaultProps = {
  size: "medium",
};
